import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import TrainingForm from "./TrainingForm"
import ParticipantsSection from "./ParticipantsSection";
import EquipmentAllocation from "./EquipmentAllocation";
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { createTraining, UpdateTraining, trainingDetails } from "../../../../services/adminApiService/batchManagementApi/BatchManagementApiService";
import PageTitle from "../../../../jsx/layouts/PageTitle";

const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";
    const dateStr = String(dateValue);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const parsed = dateStr.split("T")[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(parsed) ? parsed : "";
};

export const schema = yup.object().shape({
    trainingName: yup.string().required("Training name is required"),
    date: yup.string().required("Date is required"),
    training_end_date: yup
        .string()
        .nullable()
        .test("end-after-start", "End date must be on or after start date", function (value) {
            const { date } = this.parent;
            if (!value || !date) return true;
            return new Date(value) >= new Date(date);
        }),
    startTime: yup.string().when("allDay", {
        is: false,
        then: (schema) => schema.required("Start time is required"),
        otherwise: (schema) => schema.nullable()
    }),

    endTime: yup.string().when("allDay", {
        is: false,
        then: (schema) => schema.required("End time is required"),
        otherwise: (schema) => schema.nullable()
    }),
    venue: yup.string().required("Venue is required"),
    supervisor: yup
        .array()
        .nullable()
        .min(1, "Supervisor is required")
});

const CreateTraining = () => {

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        getValues,
        clearErrors,
        control,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            supervisor: [],
            date: "",
            training_end_date: "",
            repeat: "none"
        }

    });

    const [selectedBatches, setSelectedBatches] = useState([]);
    const [selectedIndividuals, setSelectedIndividuals] = useState([]);
    const [selectedEquipments, setSelectedEquipments] = useState([]);
    const [initialEquipments, setInitialEquipments] = useState([]);

    const [saving, setSaving] = useState(false);
    const [selectedSupervisors, setSelectedSupervisors] = useState([]);

    const navigate = useNavigate();
    const { id } = useParams();


    const isEditMode = Boolean(id);

    /* ================= LOAD EDIT DATA ================= */
    useEffect(() => {
        const fetchTrainingDetails = async () => {
            if (!isEditMode) return;

            try {
                const response = await trainingDetails(id);
                const details = response?.data?.data;

                if (!details) return;

                // FORM VALUES
                setValue("trainingName", details?.training_name);
                setValue("date", formatDateForInput(details?.training_date));
                setValue("training_end_date", formatDateForInput(details?.training_end_date));
                setValue("startTime", details?.start_time?.slice(0, 5) || "");
                setValue("endTime", details?.end_time?.slice(0, 5) || "");
                setValue("venue", details?.venue_id != null ? String(details.venue_id) : "");
                setValue("description", details?.description || "");
                setValue("repeat", details?.repeat_type || "none");
                setValue("allDay", Boolean(details?.is_all_day));

                // SUPERVISORS
                setSelectedSupervisors(details?.supervisors || []);

                setValue(
                    "supervisor",
                    details?.supervisors?.map((s) => Number(s.id))
                );

                // PARTICIPANTS
                setSelectedBatches(
                    details?.batches?.map(b => ({
                        batch_id: b.id,
                        batch_name: b.batch_name
                    }))
                );

                setSelectedIndividuals(
                    details?.athletes?.map(a => ({
                        id: a.id,
                        first_name: a.first_name,
                        last_name: a.last_name
                    }))
                );

                setSelectedEquipments(
                    details?.equipments?.map(e => ({
                        equipment_id: e.inventory_item_id,
                        quantity: e.quantity
                    }))
                );
                setInitialEquipments(details?.equipments || []);

            } catch (err) {
                console.error("Error loading training details:", err);
            }
        };

        fetchTrainingDetails();
    }, [id, isEditMode, setValue]);



    const onSubmit = async (data) => {

        if (selectedBatches.length === 0 && selectedIndividuals.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "Validation Required",
                text: "Please select at least one batch or individual athlete",
                confirmButtonColor: "#7c3aed"
            });
            return;
        }

        try {

            setSaving(true);

            const formValues = getValues();

            const payload = {
                training_name: data?.trainingName,
                training_date: data?.date ?? formValues.date ?? "",
                training_end_date: data?.training_end_date ?? formValues.training_end_date ?? "",
                start_time: data?.startTime ?? formValues.startTime ?? "",
                end_time: data?.endTime ?? formValues.endTime ?? "",
                venue_id: Number(data?.venue ?? formValues.venue),
                description: data?.description || "",
                supervisors: data?.supervisor,

                batches: selectedBatches
                    .map((b) => b?.batch_id)
                    .filter(Boolean),

                individual_athletes: selectedIndividuals
                    .map((a) => a?.id)
                    .filter(Boolean),

                equipments: selectedEquipments,

                repeat_type: data?.repeat || '',
                is_all_day: data?.allDay || ''
            };

            let response;

            if (isEditMode) {
                response = await UpdateTraining(id, payload);
            } else {
                response = await createTraining(payload);
            }

            // SUCCESS
            if (response?.data?.success === true) {

                await Swal.fire({
                    icon: "success",
                    title: "Success",
                    text:
                        response?.data?.message ||
                        (isEditMode
                            ? "Training updated successfully"
                            : "Training created successfully"),
                    timer: 2000,
                    showConfirmButton: false,
                });

                navigate("/batch-management/calendar");

            } else {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response?.data?.message || "Something went wrong",
                });

            }

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong!",
            });

        } finally {
            setSaving(false);
        }
    };

    const startDate = watch("date");
    const endDate = watch("training_end_date");

    return (
        <>
            <PageTitle
                activeMenu={isEditMode ? "Update Training" : "Create Training"}
                motherMenu="Manage Calendar " />

            <form onSubmit={handleSubmit(onSubmit)}>

                <TrainingForm
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    control={control}
                    selectedSupervisors={selectedSupervisors}
                    setSelectedSupervisors={setSelectedSupervisors}
                    isEditMode={isEditMode}
                />

                <ParticipantsSection
                    selectedBatches={selectedBatches}
                    setSelectedBatches={setSelectedBatches}
                    selectedIndividuals={selectedIndividuals}
                    setSelectedIndividuals={setSelectedIndividuals}
                />

                <EquipmentAllocation
                    setSelectedEquipments={setSelectedEquipments}
                    initialEquipments={initialEquipments}
                    startDate={startDate}
                    endDate={endDate}

                />

            </form>
        </>

    );
};

export default CreateTraining;